import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, MessageSquare, Clock, User, Tag, Calendar, Link2,
  AlertCircle, Pencil, Save, Plus, XCircle,
  MoreVertical, Trash2, ArrowLeft, ArrowRightLeft, Settings2, Maximize2,
} from 'lucide-react';
import { useIssue, useTransitionIssue, useUpdateIssue, useDeleteIssue, useActivityLogs } from '../../hooks/useIssues';
import { PRIORITY_BADGE, getContrastText } from './IssueListView';
import { resolveIssueTypeConfig } from '../../constants/issueTypeConfig';
import { useProjectIssueTypes } from '../../hooks/useProjectIssueTypes';
import { useFieldConfig } from '../../hooks/useIssueTypeFieldConfig';
import IssueDetailFields from './IssueDetailFields';
import IssueMoveDialog from './IssueMoveDialog';
import SourceMessageMeta from '../chat/SourceMessageMeta';
import { useProjects } from '../../hooks/useProjects';
import StatusTransitionDropdown from './StatusTransitionDropdown';
import IssueAttachmentSection from './IssueAttachmentSection';
import DeleteIssueModal from './DeleteIssueModal';
import IssueLinkModal from './IssueLinkModal';
import IssueTemplateSelector from './IssueTemplateSelector';
import GitHubDevPanel from '../github/GitHubDevPanel';
import IssueTraceSection from '../github/IssueTraceSection';
import IssueDesignSection from '../design/IssueDesignSection';
import IssueReleaseSection from '../release/IssueReleaseSection';
import IssueComments from './IssueComments';
import Spinner from '../ui/Spinner';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { getSocket, SOCKET_EVENTS } from '../../lib/socket';
import { useResolvedWorkflowStatuses } from '../../hooks/useResolvedWorkflow';
import type { ProjectMember, WorkflowStatusInfo } from '../../services/projectService';

interface IssueDetailPanelProps {
  projectId: string;
  projectKey?: string; // [Full Page] 버튼용
  issueId: string;
  onClose: () => void;
  members?: ProjectMember[];
  workflowStatuses?: WorkflowStatusInfo[];
  projectWorkflow?: { workflowId: string; name: string; statuses: WorkflowStatusInfo[] };
}

const ACTIVITY_ACTION_LABELS: Record<string, string> = {
  ISSUE_CREATED: '작업 생성',
  ISSUE_UPDATED: '작업 수정',
  ISSUE_TRANSITIONED: '상태 전이',
  ISSUE_COMMENTED: '코멘트 추가',
  ISSUE_DELETED: '작업 삭제',
  // GitHub (CHG-046)
  GITHUB_COMMIT_LINKED: '커밋 연결',
  GITHUB_BRANCH_CREATED: '브랜치 생성',
  GITHUB_PR_CREATED: 'PR 생성',
  GITHUB_PR_OPENED: 'PR 열림',
  GITHUB_PR_CLOSED: 'PR 닫힘',
  GITHUB_PR_MERGED: 'PR 머지',
  GITHUB_PR_REOPENED: 'PR 재오픈',
  GITHUB_TRANSITION_FAILED: '자동 전환 실패',
};

/** @deprecated CHG-052: useProjectIssueTypes 훅에서 동적 로딩 */
const FALLBACK_ISSUE_TYPE_OPTIONS = [
  { value: 'Bug', label: 'Bug' },
  { value: 'Task', label: 'Task' },
  { value: 'Story', label: 'Story' },
  { value: 'Epic', label: 'Epic' },
  { value: 'Subtask', label: 'Subtask' },
];

const PRIORITY_OPTIONS = [
  { value: 'BLOCKER', label: '블로커' },
  { value: 'CRITICAL', label: '긴급' },
  { value: 'HIGH', label: '높음' },
  { value: 'MEDIUM', label: '보통' },
  { value: 'LOW', label: '낮음' },
];

// CHG-011: 필드명 한글 매핑
const FIELD_LABELS: Record<string, string> = {
  title: '제목',
  description: '설명',
  issueType: '작업 유형',
  priority: '우선순위',
  assigneeId: '담당자',
  statusId: '상태',
  labels: '레이블',
  startDate: '시작일',
  dueDate: '마감일',
  storyPoints: '스토리 포인트',
  resolution: '해결 방법',
};

export default function IssueDetailPanel({ projectId, projectKey, issueId, onClose, members, workflowStatuses, projectWorkflow }: IssueDetailPanelProps) {
  // REQ-V24-011: Internal navigation stack
  const [issueStack, setIssueStack] = useState<string[]>([]);
  const [currentIssueId, setCurrentIssueId] = useState(issueId);

  // Reset when external issueId changes
  useEffect(() => {
    setCurrentIssueId(issueId);
    setIssueStack([]);
  }, [issueId]);

  const navigateToIssue = useCallback((targetCuidId: string) => {
    setIssueStack((prev) => [...prev, currentIssueId]);
    setCurrentIssueId(targetCuidId);
  }, [currentIssueId]);

  const goBack = useCallback(() => {
    if (issueStack.length === 0) return;
    const prevId = issueStack[issueStack.length - 1];
    setIssueStack((prev) => prev.slice(0, -1));
    setCurrentIssueId(prevId);
  }, [issueStack]);

  const canGoBack = issueStack.length > 0;

  const { data: issue, isLoading } = useIssue(projectId, currentIssueId);
  const { data: activities } = useActivityLogs(projectId, currentIssueId);
  const { data: projectIssueTypes } = useProjectIssueTypes(projectId); // CHG-052
  const transitionMutation = useTransitionIssue(projectId);
  const updateMutation = useUpdateIssue(projectId);
  const deleteMutation = useDeleteIssue(projectId);

  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'comments' | 'activity'>('comments');

  // REQ-V18-001: 더보기 메뉴 상태
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const { data: allProjects } = useProjects();
  const menuRef = useRef<HTMLDivElement>(null);

  // CHG-011: 편집 모드 상태
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIssueType, setEditIssueType] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editAssigneeId, setEditAssigneeId] = useState('');
  const [editStatusId, setEditStatusId] = useState('');
  const [editLabels, setEditLabels] = useState<string[]>([]);
  const [editLabelInput, setEditLabelInput] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editStoryPoints, setEditStoryPoints] = useState('');
  const [editCustomFields, setEditCustomFields] = useState<Record<string, unknown>>({});

  // CHG-053: 필드 설정
  const { data: fieldConfig } = useFieldConfig((issue as any)?.issueTypeId);

  // 편집 모드 초기화
  const initEditForm = useCallback(() => {
    if (!issue) return;
    setEditTitle(issue.title);
    setEditDescription(issue.description ?? '');
    setEditIssueType(issue.issueType);
    setEditPriority(issue.priority);
    setEditAssigneeId(issue.assignee?.id ?? '');
    setEditStatusId(issue.statusId);
    setEditLabels([...issue.labels]);
    setEditStartDate(issue.startDate ? issue.startDate.slice(0, 10) : '');
    setEditDueDate(issue.dueDate ? issue.dueDate.slice(0, 10) : '');
    setEditStoryPoints(issue.storyPoints != null ? String(issue.storyPoints) : '');
    setEditCustomFields(((issue as any).customFields as Record<string, unknown>) ?? {});
  }, [issue]);

  useEffect(() => {
    if (issue) initEditForm();
  }, [issue, initEditForm]);

  // CHG-042: 작업 유형별 유효 워크플로우에서 상태 목록 결정
  const resolvedWf = useResolvedWorkflowStatuses(projectId, issue?.issueType, projectWorkflow);
  // 편집 모드에서 issueType 변경 시 유효 워크플로우도 변경
  const editResolvedWf = useResolvedWorkflowStatuses(projectId, editIssueType || issue?.issueType, projectWorkflow);

  // CHG-042: issueType 변경 시 워크플로우가 다르면 상태를 자동 조정
  useEffect(() => {
    if (!isEditing || !issue) return;
    if (editIssueType === issue.issueType) return;
    if (editResolvedWf.statuses.length === 0) return;
    const validInNew = editResolvedWf.statuses.some((s) => s.statusId === editStatusId);
    if (!validInNew) {
      const currentStatus = resolvedWf.statuses.find((s) => s.statusId === editStatusId);
      const category = currentStatus?.category ?? 'TODO';
      const fallback = editResolvedWf.statuses.find((s) => s.category === category)
        ?? editResolvedWf.statuses.find((s) => s.category === 'TODO')
        ?? editResolvedWf.statuses[0];
      if (fallback) setEditStatusId(fallback.statusId);
    }
  }, [editIssueType, editResolvedWf.statuses, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // 더보기 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // REQ-V18-011: 다른 사용자가 현재 이슈를 삭제한 경우 처리
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleIssueDeleted = (data: { issueId: string; issueKey: string }) => {
      if (data.issueId === issueId) {
        toast('이 작업이 삭제되었습니다.', { icon: '🗑️' });
        onClose();
      }
    };

    socket.on(SOCKET_EVENTS.ISSUE_DELETED, handleIssueDeleted);
    return () => {
      socket.off(SOCKET_EVENTS.ISSUE_DELETED, handleIssueDeleted);
    };
  }, [issueId, onClose]);

  if (isLoading) {
    return (
      <div className="fixed inset-y-0 right-0 w-[560px] bg-white shadow-2xl border-l border-gray-200 z-40 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!issue) {
    return (
      <div className="fixed inset-y-0 right-0 w-[560px] bg-white shadow-2xl border-l border-gray-200 z-40 flex items-center justify-center">
        <div className="text-gray-400">작업을 찾을 수 없습니다.</div>
      </div>
    );
  }

  const effectiveStatuses = resolvedWf.statuses.length > 0 ? resolvedWf.statuses : (workflowStatuses ?? []);
  const editEffectiveStatuses = editResolvedWf.statuses.length > 0 ? editResolvedWf.statuses : (workflowStatuses ?? []);

  const priority = PRIORITY_BADGE[issue.priority];

  const handleTransition = async (newStatusId: string) => {
    const targetStatus = effectiveStatuses.find((ws) => ws.statusId === newStatusId);
    try {
      await transitionMutation.mutateAsync({ issueId: issue.id, statusId: newStatusId });
      toast.success(`상태가 ${targetStatus?.name ?? newStatusId}(으)로 변경되었습니다.`);
    } catch {
      toast.error('상태 전이에 실패했습니다.');
    }
  };

  const handleStartEdit = () => {
    initEditForm();
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    initEditForm();
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) {
      toast.error('제목을 입력해주세요.');
      return;
    }

    const data: Record<string, unknown> = {};
    if (editTitle !== issue.title) data.title = editTitle.trim();
    if (editDescription !== (issue.description ?? '')) data.description = editDescription.trim() || null;
    if (editIssueType !== issue.issueType) data.issueType = editIssueType;
    if (editPriority !== issue.priority) data.priority = editPriority;
    if (editAssigneeId !== (issue.assignee?.id ?? '')) data.assigneeId = editAssigneeId || null;
    if (editStatusId !== issue.statusId) data.statusId = editStatusId;
    if (JSON.stringify(editLabels.sort()) !== JSON.stringify([...issue.labels].sort())) data.labels = editLabels;
    const currentStartDate = issue.startDate ? issue.startDate.slice(0, 10) : '';
    if (editStartDate !== currentStartDate) data.startDate = editStartDate || null;
    const currentDueDate = issue.dueDate ? issue.dueDate.slice(0, 10) : '';
    if (editDueDate !== currentDueDate) data.dueDate = editDueDate || null;
    const currentSp = issue.storyPoints != null ? String(issue.storyPoints) : '';
    if (editStoryPoints !== currentSp) data.storyPoints = editStoryPoints ? Number(editStoryPoints) : null;

    // CHG-053: 커스텀 필드 변경 확인
    const existingCf = ((issue as any).customFields as Record<string, unknown>) ?? {};
    if (JSON.stringify(editCustomFields) !== JSON.stringify(existingCf)) {
      data.customFields = editCustomFields;
    }

    if (Object.keys(data).length === 0) {
      toast('변경된 내용이 없습니다.');
      setIsEditing(false);
      return;
    }

    // CHG-042: issueType 변경 시 워크플로우가 다르면 statusId도 함께 전달
    if (data.issueType && data.issueType !== issue.issueType) {
      // 워크플로우가 다르면 statusId가 data에 포함되어 있어야 함 (편집 시 드롭다운에서 선택됨)
      // 서버가 자동 매핑 처리하므로 statusId가 없어도 OK
      if (data.statusId) {
        // issueType + statusId를 함께 update API로 전달 (서버가 검증)
        // transition API는 사용하지 않음
      }
    } else {
      // 상태 변경은 transition API로 분리 처리 (issueType이 안 바뀌는 경우)
      if (data.statusId) {
        try {
          await transitionMutation.mutateAsync({ issueId: issue.id, statusId: data.statusId as string });
        } catch {
          toast.error('상태 전이에 실패했습니다.');
          return;
        }
        delete data.statusId;
      }
    }

    // 나머지 필드 업데이트
    if (Object.keys(data).length > 0) {
      try {
        await updateMutation.mutateAsync({ issueId: issue.id, data });
        toast.success('작업이 수정되었습니다.');
      } catch {
        toast.error('작업 수정에 실패했습니다.');
        return;
      }
    } else {
      toast.success('작업이 수정되었습니다.');
    }

    setIsEditing(false);
  };

  const handleAddLabel = () => {
    const newLabel = editLabelInput.trim();
    if (!newLabel || editLabels.includes(newLabel)) return;
    setEditLabels((prev) => [...prev, newLabel]);
    setEditLabelInput('');
  };

  const handleRemoveLabel = (label: string) => {
    setEditLabels((prev) => prev.filter((l) => l !== label));
  };

  // REQ-V18-002: 삭제 권한 확인
  const currentMember = members?.find((m) => m.userId === user?.id);
  // CHG-043 REQ-V43-005: STAKEHOLDER 읽기 전용 — 이슈 편집/상태전환 불가, 댓글 가능
  const isStakeholder = currentMember?.projectRole === 'STAKEHOLDER' && user?.role !== 'SUPER_ADMIN';
  const canEdit = !isStakeholder;
  const canDelete =
    !isStakeholder && (
      issue.reporter?.id === user?.id ||
      user?.role === 'SUPER_ADMIN' ||
      currentMember?.projectRole === 'PROJECT_LEADER' ||
      currentMember?.projectRole === 'PROJECT_MANAGER'
    );

  // REQ-V18-004: 삭제 처리
  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(issue.id);
      toast.success('작업이 삭제되었습니다.');
      setShowDeleteModal(false);
      onClose();
    } catch (err) {
      const error = err as { response?: { status?: number } };
      if (error.response?.status === 403) {
        toast.error('작업을 삭제할 권한이 없습니다.');
      } else {
        toast.error('작업 삭제에 실패했습니다.');
      }
      throw err;
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('ko-KR', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const formatRelativeTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return '방금 전';
    if (diffMin < 60) return `${diffMin}분 전`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}시간 전`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay}일 전`;
    return formatDate(dateStr);
  };

  // CHG-011: 활동 로그에서 ISSUE_UPDATED의 변경 이력 렌더링
  const renderActivityChanges = (log: { action: string; before: Record<string, unknown> | null; after: Record<string, unknown> | null }) => {
    if (log.action !== 'ISSUE_UPDATED' || !log.after) return null;
    const afterData = log.after as { changes?: { field: string; from: string | null; to: string | null }[] };
    if (!afterData.changes || !Array.isArray(afterData.changes)) return null;

    return (
      <div className="mt-1 space-y-0.5">
        {afterData.changes.map((change, idx) => {
          const fieldLabel = FIELD_LABELS[change.field] ?? change.field;
          const fromDisplay = formatFieldValue(change.field, change.from);
          const toDisplay = formatFieldValue(change.field, change.to);
          return (
            <div key={idx} className="text-sm text-gray-500">
              {fieldLabel}을(를) <span className="text-gray-400 line-through">{fromDisplay || '(없음)'}</span>에서{' '}
              <span className="font-medium text-primary-600">{toDisplay || '(없음)'}</span>(으)로 변경
            </div>
          );
        })}
      </div>
    );
  };

  // 필드 값을 사용자 친화적으로 표시
  const formatFieldValue = (field: string, value: string | null): string => {
    if (value === null || value === '' || value === 'null') return '';
    if (field === 'priority') {
      return PRIORITY_OPTIONS.find((o) => o.value === value)?.label ?? value;
    }
    if (field === 'issueType') {
      return projectIssueTypes?.issueTypes.find((t) => t.name === value)?.name ?? value;
    }
    if (field === 'statusId') {
      const ws = workflowStatuses?.find((s) => s.statusId === value);
      return ws?.name ?? value;
    }
    if (field === 'assigneeId' && members) {
      const member = members.find((m) => m.userId === value);
      return member?.user.name ?? value;
    }
    return value;
  };

  return (
    <div className="fixed inset-y-0 right-0 w-[560px] bg-white shadow-2xl border-l border-gray-200 z-40 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center gap-2">
          {/* REQ-V24-011: Back button */}
          {canGoBack && (
            <button
              onClick={goBack}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              title="이전 작업으로 돌아가기"
            >
              <ArrowLeft size={16} className="text-gray-500" />
            </button>
          )}
          {(() => {
            const cfg = resolveIssueTypeConfig(issue.issueTypeDef, issue.issueType);
            return <cfg.Icon size={16} className={cfg.color} />;
          })()}
          <span className="font-mono text-sm text-gray-500">{issue.issueId}</span>
          <span
            className="px-2 py-0.5 text-[11px] font-medium rounded"
            style={{
              backgroundColor: issue.workflowStatus.color,
              color: getContrastText(issue.workflowStatus.color),
            }}
          >
            {issue.workflowStatus.name}
          </span>
          {/* 풀페이지로 확장 (projectKey가 있을 때만 노출) */}
          {projectKey && (
            <button
              onClick={() => {
                onClose();
                navigate(`/projects/${projectKey}/issues/${currentIssueId}`);
              }}
              title="풀 페이지로 보기"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
            >
              <Maximize2 size={11} />
              Full Page
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {/* CHG-011: 수정/저장/취소 버튼 (CHG-043: STAKEHOLDER는 숨김) */}
          {!isEditing ? (
            canEdit && (
              <button
                onClick={handleStartEdit}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <Pencil size={13} />
                수정
              </button>
            )
          ) : (
            <>
              <button
                onClick={handleCancelEdit}
                className="px-2.5 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={updateMutation.isPending || transitionMutation.isPending}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
              >
                <Save size={13} />
                저장
              </button>
            </>
          )}
          {/* REQ-V18-001 + REQ-V24-012: 더보기(⋮) 메뉴 */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
              aria-label="더보기"
            >
              <MoreVertical size={18} className="text-gray-500" />
            </button>
            {isMenuOpen && (
              <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                {canEdit && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setShowLinkModal(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Link2 size={14} />
                    작업 연결하기
                  </button>
                )}
                {/* 필드 설정 (해당 이슈 타입) */}
                {canEdit && (issue as any).issueTypeId && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      navigate(`/settings/issue-types/${(issue as any).issueTypeId}/field-config`);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Settings2 size={14} />
                    필드 설정
                  </button>
                )}
                {/* CHG-054: 프로젝트 이동 */}
                {canEdit && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setShowMoveDialog(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <ArrowRightLeft size={14} />
                    프로젝트 이동
                  </button>
                )}
                {canDelete && (
                  <>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={() => {
                        setIsMenuOpen(false);
                        setShowDeleteModal(true);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 size={14} />
                      삭제
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Title */}
        <div className="px-5 pt-4 pb-2">
          {isEditing ? (
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="w-full text-lg font-bold text-gray-900 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none"
            />
          ) : (
            <h2 className="text-lg font-bold text-gray-900 leading-snug">{issue.title}</h2>
          )}
        </div>

        {/* State transition dropdown (읽기 모드 + 편집 가능한 사용자만) */}
        {!isEditing && canEdit && (
          <div className="px-5 pb-3">
            <StatusTransitionDropdown
              projectId={projectId}
              issueId={issue.id}
              currentStatusName={issue.workflowStatus.name}
              currentStatusColor={issue.workflowStatus.color}
              onTransition={(newStatusId) => handleTransition(newStatusId)}
              disabled={transitionMutation.isPending}
            />
          </div>
        )}

        {/* REQ-V24-008: Parent issue */}
        {issue.parentIssue && (
          <div className="px-5 py-2 border-t border-gray-100">
            <span className="text-sm text-gray-500 mr-2">상위 작업</span>
            <button
              onClick={() => navigateToIssue(issue.parentIssue!.id)}
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 hover:underline"
            >
              {(() => {
                const typeConf = resolveIssueTypeConfig(issue.parentIssue.issueTypeDef, issue.parentIssue.issueType);
                return <typeConf.Icon size={14} className={typeConf.color} style={typeConf.hexColor ? { color: typeConf.hexColor } : undefined} />;
              })()}
              <span className="font-mono text-sm">{issue.parentIssue.issueId}</span>
              <span className="truncate max-w-[280px]">{issue.parentIssue.title}</span>
            </button>
          </div>
        )}

        {/* Metadata grid */}
        <div className="px-5 py-3 border-t border-gray-100">
          {isEditing ? (
            <div className="space-y-3">
              {/* 유형 + 우선순위 */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">작업 유형</label>
                  <select
                    value={editIssueType}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setEditIssueType(newType);
                      // CHG-042: issueType 변경 시 워크플로우가 다르면 상태를 자동 조정
                      // editResolvedWf는 다음 렌더에서 업데이트되므로, 여기서는 현재 상태가 새 워크플로우에 있는지 체크
                    }}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                  >
                    {(projectIssueTypes?.issueTypes ?? FALLBACK_ISSUE_TYPE_OPTIONS).map((o) => (
                      <option key={'issueTypeId' in o ? o.issueTypeId : o.value} value={'name' in o && 'issueTypeId' in o ? o.name : (o as any).value}>{('name' in o && 'issueTypeId' in o) ? o.name : (o as any).label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">우선순위</label>
                  <select
                    value={editPriority}
                    onChange={(e) => setEditPriority(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                  >
                    {PRIORITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 담당자 + 상태 */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">담당자</label>
                  <select
                    value={editAssigneeId}
                    onChange={(e) => setEditAssigneeId(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                  >
                    <option value="">미할당</option>
                    {(members ?? []).map((m) => (
                      <option key={m.userId} value={m.userId}>{m.user.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">상태</label>
                  <select
                    value={editStatusId}
                    onChange={(e) => setEditStatusId(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
                  >
                    {editEffectiveStatuses.map((ws) => (
                      <option key={ws.statusId} value={ws.statusId}>{ws.name}</option>
                    ))}
                  </select>
                  {editIssueType !== issue.issueType && resolvedWf.workflowId !== editResolvedWf.workflowId && (
                    <p className="text-[11px] text-amber-600 mt-0.5">
                      워크플로우가 변경됩니다 ({editResolvedWf.workflowName})
                    </p>
                  )}
                </div>
              </div>

              {/* 시작일 + 마감일 */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">시작일</label>
                  <input
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">마감일</label>
                  <input
                    type="date"
                    value={editDueDate}
                    onChange={(e) => setEditDueDate(e.target.value)}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </div>
              </div>

              {/* 스토리 포인트 */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">스토리 포인트</label>
                  <input
                    type="number"
                    min={0}
                    value={editStoryPoints}
                    onChange={(e) => setEditStoryPoints(e.target.value)}
                    placeholder="0"
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </div>
              </div>

              {/* 레이블 */}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">레이블</label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {editLabels.map((l) => (
                    <span key={l} className="inline-flex items-center gap-1 px-2 py-0.5 text-sm bg-gray-100 text-gray-600 rounded-full">
                      {l}
                      <button onClick={() => handleRemoveLabel(l)} className="hover:text-red-500">
                        <XCircle size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={editLabelInput}
                    onChange={(e) => setEditLabelInput(e.target.value)}
                    onKeyDown={(e) => { if (e.nativeEvent.isComposing || e.keyCode === 229) return; if (e.key === 'Enter') { e.preventDefault(); handleAddLabel(); } }}
                    placeholder="레이블 입력 후 Enter"
                    className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                  <button
                    onClick={handleAddLabel}
                    className="px-2 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <MetaField
                  icon={<AlertCircle size={14} />}
                  label="우선순위"
                  value={
                    <span className={`px-2 py-0.5 text-[11px] font-medium rounded ${priority?.class ?? ''}`}>
                      {priority?.label ?? issue.priority}
                    </span>
                  }
                />
                <MetaField
                  icon={<Tag size={14} />}
                  label="유형"
                  value={
                    (() => {
                      const cfg = resolveIssueTypeConfig(issue.issueTypeDef, issue.issueType);
                      return (
                        <span className="inline-flex items-center gap-1">
                          <cfg.Icon size={14} className={cfg.color} />
                          {cfg.label}
                        </span>
                      );
                    })()
                  }
                />
                <MetaField
                  icon={<User size={14} />}
                  label="담당자"
                  value={issue.assignee?.name ?? '미할당'}
                />
                <MetaField
                  icon={<User size={14} />}
                  label="보고자"
                  value={issue.reporter?.name ?? '-'}
                />
                <MetaField
                  icon={<Calendar size={14} />}
                  label="시작일"
                  value={issue.startDate ? new Date(issue.startDate).toLocaleDateString('ko-KR') : '미설정'}
                />
                <MetaField
                  icon={<Calendar size={14} />}
                  label="마감일"
                  value={issue.dueDate ? new Date(issue.dueDate).toLocaleDateString('ko-KR') : '미설정'}
                />
                <MetaField
                  icon={<Clock size={14} />}
                  label="생성일"
                  value={formatDate(issue.createdAt)}
                />
                {issue.storyPoints != null && (
                  <MetaField label="스토리 포인트" value={String(issue.storyPoints)} />
                )}
                {issue.resolution && (
                  <MetaField label="해결 방법" value={issue.resolution} />
                )}
              </div>

              {/* Labels */}
              {issue.labels.length > 0 && (
                <div className="mt-3">
                  <span className="text-sm font-medium text-gray-500">레이블</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {issue.labels.map((l) => (
                      <span key={l} className="px-2 py-0.5 text-sm bg-gray-100 text-gray-600 rounded-full">
                        {l}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* CHG-123 REQ-V123-052: 채팅 메시지 출처 */}
        {issue.sourceMessage && (
          <div className="px-5 py-2 border-t border-gray-100">
            <SourceMessageMeta sourceMessage={issue.sourceMessage} />
          </div>
        )}

        {/* Description */}
        <div className="px-5 py-3 border-t border-gray-100">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">설명</h3>
          {isEditing ? (
            <div>
              <div className="mb-1.5">
                <IssueTemplateSelector
                  projectId={projectId}
                  onSelectTemplate={(content, templateName) => {
                    setEditDescription((prev) => {
                      if (!prev || prev.trim() === '') return content;
                      return prev + '\n\n' + content;
                    });
                    toast.success(`'${templateName}' 템플릿이 적용되었습니다.`);
                  }}
                />
              </div>
              <textarea
                ref={(el) => {
                  if (el) {
                    el.style.height = 'auto';
                    el.style.height = Math.max(120, el.scrollHeight) + 'px';
                  }
                }}
                value={editDescription}
                onChange={(e) => {
                  setEditDescription(e.target.value);
                  const el = e.target;
                  el.style.height = 'auto';
                  el.style.height = Math.max(120, el.scrollHeight) + 'px';
                }}
                placeholder="작업 설명을 입력하세요..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none resize-y"
              />
            </div>
          ) : issue.description ? (
            <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed prose prose-sm max-w-none">
              {issue.description}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">설명이 없습니다.</p>
          )}
        </div>

        {/* CHG-053: 커스텀 필드 동적 렌더링 */}
        {fieldConfig && fieldConfig.sections.some((s) => s.fields.some((f) => !f.field.isSystem)) && (
          <div className="px-5 py-3 border-t border-gray-100">
            <IssueDetailFields
              issue={isEditing
                ? { ...(issue as unknown as Record<string, unknown>), customFields: editCustomFields }
                : (issue as unknown as Record<string, unknown>)}
              sections={fieldConfig.sections
                .map((s) => ({ ...s, fields: s.fields.filter((f) => !f.field.isSystem) }))
                .filter((s) => s.fields.length > 0)}
              isEditing={isEditing}
              onChange={isEditing ? (fieldKey, value, isSystem) => {
                if (!isSystem) {
                  setEditCustomFields((prev) => ({ ...prev, [fieldKey]: value }));
                }
              } : undefined}
              members={members?.map((m) => ({ id: m.user.id, name: m.user.name }))}
            />
          </div>
        )}

        {/* REQ-V24-009: Child issues */}
        {issue.childIssues && issue.childIssues.length > 0 && (
          <div className="px-5 py-3 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              하위 작업 ({issue.childIssues.length})
            </h3>
            <div className="space-y-1">
              {issue.childIssues.map((child) => {
                const typeConf = resolveIssueTypeConfig(child.issueTypeDef, child.issueType);
                return (
                  <button
                    key={child.id}
                    onClick={() => navigateToIssue(child.id)}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
                  >
                    <typeConf.Icon size={14} className={typeConf.color} style={typeConf.hexColor ? { color: typeConf.hexColor } : undefined} />
                    <span className="font-mono text-sm text-gray-500">{child.issueId}</span>
                    <span className="text-sm text-gray-900 truncate flex-1">{child.title}</span>
                    <span
                      className="px-1.5 py-0.5 text-[11px] font-medium rounded"
                      style={{
                        backgroundColor: child.workflowStatus.color,
                        color: getContrastText(child.workflowStatus.color),
                      }}
                    >
                      {child.workflowStatus.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Attachments */}
        <IssueAttachmentSection
          projectId={projectId}
          issueId={issue.id}
          attachments={issue.issueAttachments ?? []}
        />

        {/* Linked entities */}
        {(issue.linkedRequirement || issue.linkedSpec || issue.linkedTestCase) && (
          <div className="px-5 py-3 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">
              연결된 항목
            </h3>
            <div className="space-y-1.5">
              {issue.linkedRequirement && (
                <LinkedItem
                  type="요구사항"
                  code={issue.linkedRequirement.reqId}
                  title={issue.linkedRequirement.title}
                />
              )}
              {issue.linkedSpec && (
                <LinkedItem
                  type="기능명세"
                  code={issue.linkedSpec.functionCode}
                  title={issue.linkedSpec.title}
                />
              )}
              {issue.linkedTestCase && (
                <LinkedItem
                  type="테스트케이스"
                  code={issue.linkedTestCase.tcId}
                  title={issue.linkedTestCase.title}
                  extra={
                    <span className={`ml-1 px-1.5 py-0.5 text-[11px] rounded ${
                      issue.linkedTestCase.status === 'PASS' ? 'bg-green-100 text-green-700' :
                      issue.linkedTestCase.status === 'FAIL' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-500'
                    }`}>
                      {issue.linkedTestCase.status}
                    </span>
                  }
                />
              )}
            </div>
          </div>
        )}

        {/* 디자인 섹션 (CHG-049: REQ-V49-020) */}
        <IssueDesignSection projectId={projectId} issueId={issue.id} />

        {/* 릴리즈 연결 — 모든 작업 유형에 추가 가능, 하위 작업 일괄 연결 옵션 제공 */}
        <IssueReleaseSection
          projectId={projectId}
          issueId={issue.id}
          hasChildren={((issue as { _count?: { childIssues?: number } })._count?.childIssues ?? 0) > 0}
        />

        {/* GitHub 개발 현황 (CHG-046: REQ-V46-013) */}
        <GitHubDevPanel projectId={projectId} issueId={issue.id} issueKey={issue.issueId} />
        <IssueTraceSection projectId={projectId} issueId={issue.id} />

        {/* Comments & Activity tabs */}
        <div className="px-5 py-3 border-t border-gray-100">
          <div className="flex border-b border-gray-200 mb-3">
            <button
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'comments'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('comments')}
            >
              <MessageSquare size={14} className="inline mr-1" />
              코멘트 ({issue.comments?.length ?? 0})
            </button>
            <button
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'activity'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('activity')}
            >
              <Clock size={14} className="inline mr-1" />
              활동 로그
            </button>
          </div>

          {activeTab === 'comments' && user && members && (
            <IssueComments
              projectId={projectId}
              issueId={issue.id}
              comments={issue.comments}
              currentUserId={user.id}
              members={members}
            />
          )}

          {activeTab === 'activity' && (
            <div className="space-y-3">
              {(!activities || activities.length === 0) && (
                <p className="text-sm text-gray-400 text-center py-4">활동 기록이 없습니다.</p>
              )}
              {activities?.map((log) => {
                const meta = (log as { metadata?: Record<string, unknown> }).metadata as Record<string, unknown> | undefined;
                const isGitHub = meta?.source === 'github';
                return (
                <div key={log.id} className="flex gap-2">
                  <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isGitHub ? 'bg-purple-400' : 'bg-gray-300'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-700">
                      {isGitHub && <span className="text-purple-500 mr-1">⚙️</span>}
                      <span className="font-medium">{log.user.name}</span>
                      {' '}
                      <span className="text-gray-500">
                        {ACTIVITY_ACTION_LABELS[log.action] ?? log.action}
                      </span>
                      {log.action === 'ISSUE_TRANSITIONED' && log.before && log.after && (
                        <span className="ml-1">
                          <span className="text-gray-400">
                            {(log.before as Record<string, string>).statusName ?? (log.before as Record<string, string>).statusId}
                          </span>
                          {' → '}
                          <span className="font-medium text-primary-600">
                            {(log.after as Record<string, string>).statusName ?? (log.after as Record<string, string>).statusId}
                          </span>
                          {isGitHub && meta?.message ? (
                            <span className="text-sm text-gray-400 ml-1">({String(meta.message).split('→')[0].trim()})</span>
                          ) : null}
                        </span>
                      )}
                      {log.action === 'GITHUB_TRANSITION_FAILED' && meta?.reason ? (
                        <span className="ml-1 text-sm text-amber-600">⚠️ {String(meta.reason)}</span>
                      ) : null}
                      {log.action === 'GITHUB_COMMIT_LINKED' && meta?.shortSha ? (
                        <span className="ml-1 text-sm font-mono text-gray-500">{String(meta.shortSha)}</span>
                      ) : null}
                      {(log.action === 'GITHUB_PR_CREATED' || log.action === 'GITHUB_PR_MERGED') && meta?.prNumber ? (
                        <span className="ml-1 text-sm font-medium text-gray-500">#{String(meta.prNumber)}</span>
                      ) : null}
                    </div>
                    {/* CHG-011: 수정 이력 상세 표시 */}
                    {renderActivityChanges(log)}
                    <div className="text-sm text-gray-400">{formatRelativeTime(log.createdAt)}</div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* REQ-V18-003: 삭제 확인 모달 */}
      <DeleteIssueModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        issueKey={issue.issueId}
      />

      {/* REQ-V24-012: 이슈 연결 모달 */}
      {showLinkModal && (
        <IssueLinkModal
          currentIssue={{
            id: issue.id,
            issueId: issue.issueId,
            issueType: issue.issueType,
            parentIssueId: issue.parentIssueId,
          }}
          projectId={projectId}
          onSuccess={() => {}}
          onClose={() => setShowLinkModal(false)}
        />
      )}

      {/* CHG-054: 이슈 이동 다이얼로그 */}
      <IssueMoveDialog
        issueId={issue.id}
        isOpen={showMoveDialog}
        onClose={() => setShowMoveDialog(false)}
        onMoved={() => {
          setShowMoveDialog(false);
          onClose();
        }}
        projects={(allProjects ?? [])
          .filter((p) => p.id !== projectId)
          .map((p) => ({ id: p.id, name: p.name, projectKey: p.projectKey }))}
      />
    </div>
  );
}

function MetaField({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-1.5">
      {icon && <span className="text-gray-400 mt-0.5">{icon}</span>}
      <div>
        <div className="text-sm text-gray-500">{label}</div>
        <div className="text-sm text-gray-900">{value}</div>
      </div>
    </div>
  );
}

function LinkedItem({
  type,
  code,
  title,
  extra,
}: {
  type: string;
  code: string;
  title: string;
  extra?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 rounded-lg text-sm">
      <Link2 size={12} className="text-gray-400 flex-shrink-0" />
      <span className="text-sm text-gray-500 flex-shrink-0">{type}</span>
      <span className="font-mono text-sm text-primary-600">{code}</span>
      <span className="text-gray-700 truncate">{title}</span>
      {extra}
    </div>
  );
}
